/**
 * Masks the Navidium widget re-injection in the cart drawer footer.
 *
 * When the cart changes the drawer replaces its own content via `replaceChildren`
 * (assets/theme.js), which re-renders the empty `<div class="nvd-mini">`
 * placeholder from sections/cart-drawer.liquid. Navidium then re-injects its
 * widget asynchronously, so the footer briefly shows the bare theme checkout
 * button before it is swapped for "Protected Checkout". This adds a skeleton state
 * over the button group for the duration of that gap.
 *
 * Both cart events are handled: a line item change dispatches `cart:change`
 * (assets/theme.js:1560) - that is the common case - while `cart:refresh` covers
 * the cart error and `pageshow` paths.
 *
 * The loading state is only ever entered when the widget has been seen to populate
 * at least once, so a disabled or failing app embed never hides the checkout
 * button, and a timeout always releases the state as a second safety net.
 */
(() => {
  const LOADING_CLASS = 'is-nvd-loading';
  const RELEASE_TIMEOUT = 4000;

  const drawer = document.getElementById('cart-drawer');

  if (!drawer) {
    return;
  }

  let widgetSeen = false;
  let releaseTimer = null;
  let staleMini = null;

  // Navidium is detected by its own `nvd-` namespace rather than by a specific
  // selector: depending on the widget version it either fills the `.nvd-mini`
  // placeholder or replaces the theme checkout button with its own node, and the
  // loader must not depend on which. `.nvd-mini` is excluded because the theme
  // renders that element itself, empty, on every refresh.
  const hasWidget = () => {
    const mini = drawer.querySelector('.nvd-mini');

    if (mini && mini.childElementCount > 0) {
      return true;
    }

    return Boolean(drawer.querySelector('[id^="nvd-"], [class*="nvd-"]:not(.nvd-mini)'));
  };

  // The drawer swaps its content asynchronously, so at the moment the cart event
  // fires the OLD widget is still on screen. Releasing on `hasWidget()` alone would
  // therefore release immediately, before the content the loader exists to mask is
  // even swapped in. The `.nvd-mini` node the theme re-renders on every refresh is
  // used as the marker: only once a NEW placeholder element exists has the swap
  // happened, and only then can the widget's presence mean it was re-injected.
  const isWidgetReinjected = () => {
    const mini = drawer.querySelector('.nvd-mini');

    if (!mini || mini === staleMini) {
      return false;
    }

    return hasWidget();
  };

  const release = () => {
    clearTimeout(releaseTimer);
    releaseTimer = null;
    staleMini = null;
    drawer.classList.remove(LOADING_CLASS);
  };

  // A single persistent observer serves both jobs: it learns whether Navidium is
  // actually injecting into this drawer, and it releases the skeleton the moment
  // the widget comes back. `.nvd-mini` itself is destroyed on every refresh, so the
  // observer watches the drawer subtree rather than the placeholder.
  new MutationObserver(() => {
    if (hasWidget()) {
      widgetSeen = true;
    }

    if (drawer.classList.contains(LOADING_CLASS) && isWidgetReinjected()) {
      release();
    }
  }).observe(drawer, { childList: true, subtree: true });

  if (hasWidget()) {
    widgetSeen = true;
  }

  const engage = () => {
    if (!widgetSeen) {
      return;
    }

    staleMini = drawer.querySelector('.nvd-mini');
    drawer.classList.add(LOADING_CLASS);
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(release, RELEASE_TIMEOUT);
  };

  document.addEventListener('cart:change', engage);
  document.addEventListener('cart:refresh', engage);
})();
